import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Sun,
  Moon,
  Menu,
  Check,
  MoreVertical,
  ChevronDown,
  LogOut,
  User,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAuth, useEncryption, useCurrency } from '@/contexts'
import { currencyOptions, type CurrencyCode } from '@/utils/currencyUtils'

interface HeaderProps {
  onMenuToggle: () => void
}

const languageOptions = [
  { value: 'en', label: 'English', flag: '🇺🇸', nativeName: 'English' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪', nativeName: 'Deutsch' },
  { value: 'fr', label: 'Français', flag: '🇫🇷', nativeName: 'Français' },
  { value: 'es', label: 'Español', flag: '🇪🇸', nativeName: 'Español' },
  { value: 'it', label: 'Italiano', flag: '🇮🇹', nativeName: 'Italiano' },
  { value: 'pl', label: 'Polski', flag: '🇵🇱', nativeName: 'Polski' },
]

export const Header = ({ onMenuToggle }: HeaderProps) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const { lock } = useEncryption()
  const { currency, setCurrency } = useCurrency()
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null
    return savedTheme || 'light'
  })
  const [isLanguageOpen, setIsLanguageOpen] = useState(false)
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileLanguageExpanded, setIsMobileLanguageExpanded] = useState(false)
  const [isMobileCurrencyExpanded, setIsMobileCurrencyExpanded] = useState(false)

  const languageRef = useRef<HTMLDivElement>(null)
  const currencyRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    document.documentElement.classList.toggle('dark', newTheme === 'dark')
    localStorage.setItem('theme', newTheme)
  }

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
    localStorage.setItem('language', lng)
    setIsLanguageOpen(false)
    setIsMobileMenuOpen(false)
  }

  const changeCurrency = (code: CurrencyCode) => {
    setCurrency(code)
    setIsCurrencyOpen(false)
    setIsMobileMenuOpen(false)
  }

  const selectedCurrency =
    currencyOptions.find((c) => c.code === currency) || currencyOptions[0]

  const handleLogout = async () => {
    setIsMobileMenuOpen(false)
    try {
      lock() // Clear DEK from memory first
      await signOut() // Sign out from Cognito
    } catch (error) {
      console.error('Logout failed:', error)
    }
    navigate('/login')
  }

  const selectedLanguage =
    languageOptions.find((lang) => lang.value === i18n.language) || languageOptions[0]

  // Apply theme to DOM on mount (theme is read from localStorage in useState initializer)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageRef.current && !languageRef.current.contains(event.target as Node)) {
        setIsLanguageOpen(false)
      }
      if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
        setIsCurrencyOpen(false)
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLanguageOpen(false)
        setIsCurrencyOpen(false)
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  return (
    <header className="relative bg-gray-900 overflow-visible">
      {/* Background decoration - subtle pink glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-64 h-64 bg-pink-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-64 h-64 bg-pink-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Menu toggle and App name */}
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              type="button"
              onClick={onMenuToggle}
              className="lg:hidden p-1.5 sm:p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              aria-label={t('common.menu', 'Toggle menu')}
            >
              <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <h1 className="text-xl font-bold font-body">
              <span className="text-white">MyPink</span>
              <span className="text-pink-400">You</span>
            </h1>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Menu Button (visible only on mobile) */}
            <div ref={mobileMenuRef} className="relative sm:hidden">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="w-10 h-10 flex items-center justify-center text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <MoreVertical className="w-5 h-5" />
              </button>

              {/* Mobile Menu Dropdown */}
              <AnimatePresence>
                {isMobileMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="fixed sm:absolute right-2 left-2 sm:left-auto sm:right-0 top-16 sm:top-auto sm:mt-2 z-50 sm:w-80 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden max-h-[calc(100vh-5rem)] overflow-y-auto"
                  >
                    <div className="py-1">
                      {/* Theme Toggle */}
                      <button
                        type="button"
                        onClick={() => {
                          toggleTheme()
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-gray-700 transition-all duration-150 group"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-700 group-hover:bg-gray-600 transition-colors">
                          {theme === 'light' ? (
                            <Moon className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors" />
                          ) : (
                            <Sun className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors" />
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                          {theme === 'light'
                            ? t('settings.darkMode', 'Dark mode')
                            : t('settings.lightMode', 'Light mode')}
                        </span>
                      </button>

                      {/* Language Selection */}
                      <div className="border-t border-gray-700 mt-1 pt-1">
                        <button
                          type="button"
                          onClick={() =>
                            setIsMobileLanguageExpanded(!isMobileLanguageExpanded)
                          }
                          className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-gray-700 transition-all duration-150 group"
                        >
                          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-700 group-hover:bg-gray-600 transition-colors">
                            <span
                              className="text-xl"
                              role="img"
                              aria-label={selectedLanguage.label}
                            >
                              {selectedLanguage.flag}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors flex-1 text-left">
                            {selectedLanguage.nativeName}
                          </span>
                          <motion.div
                            animate={{ rotate: isMobileLanguageExpanded ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                          </motion.div>
                        </button>

                        <AnimatePresence>
                          {isMobileLanguageExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <div className="px-2 py-1 bg-gray-900/50">
                                {languageOptions.map((language) => {
                                  const isSelected = language.value === i18n.language
                                  if (isSelected) return null
                                  return (
                                    <button
                                      key={language.value}
                                      type="button"
                                      onClick={() => {
                                        changeLanguage(language.value)
                                        setIsMobileLanguageExpanded(false)
                                      }}
                                      className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-700 rounded-lg transition-all duration-150 group"
                                    >
                                      <span
                                        className="text-lg"
                                        role="img"
                                        aria-label={language.label}
                                      >
                                        {language.flag}
                                      </span>
                                      <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                                        {language.nativeName}
                                      </span>
                                    </button>
                                  )
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Currency Selection */}
                      <div className="border-t border-gray-700 mt-1 pt-1">
                        <button
                          type="button"
                          onClick={() =>
                            setIsMobileCurrencyExpanded(!isMobileCurrencyExpanded)
                          }
                          className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-gray-700 transition-all duration-150 group"
                        >
                          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-700 group-hover:bg-gray-600 transition-colors">
                            <span
                              className="text-xl"
                              role="img"
                              aria-label={selectedCurrency.name}
                            >
                              {selectedCurrency.flag}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors flex-1 text-left">
                            {selectedCurrency.code} ({selectedCurrency.symbol})
                          </span>
                          <motion.div
                            animate={{ rotate: isMobileCurrencyExpanded ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                          </motion.div>
                        </button>

                        <AnimatePresence>
                          {isMobileCurrencyExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <div className="px-2 py-1 bg-gray-900/50">
                                {currencyOptions.map((curr) => {
                                  const isSelected = curr.code === currency
                                  if (isSelected) return null
                                  return (
                                    <button
                                      key={curr.code}
                                      type="button"
                                      onClick={() => {
                                        changeCurrency(curr.code)
                                        setIsMobileCurrencyExpanded(false)
                                      }}
                                      className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-700 rounded-lg transition-all duration-150 group"
                                    >
                                      <span
                                        className="text-lg"
                                        role="img"
                                        aria-label={curr.name}
                                      >
                                        {curr.flag}
                                      </span>
                                      <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                                        {curr.code} ({curr.symbol})
                                      </span>
                                    </button>
                                  )
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-gray-700 mt-1 pt-1">
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-gray-700 transition-all duration-150 group"
                        >
                          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-900/30 group-hover:bg-red-900/50 transition-colors">
                            <LogOut className="w-4 h-4 text-red-400" />
                          </div>
                          <span className="text-sm font-medium text-red-400 group-hover:text-red-300 transition-colors">
                            {t('auth.logout', 'Log out')}
                          </span>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop Controls */}
            {/* Language Selector */}
            <div ref={languageRef} className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2"
                aria-haspopup="listbox"
                aria-expanded={isLanguageOpen}
              >
                <span
                  className="text-lg flex-shrink-0"
                  role="img"
                  aria-label={selectedLanguage.label}
                >
                  {selectedLanguage.flag}
                </span>
                <span className="whitespace-nowrap">{selectedLanguage.nativeName}</span>
              </button>

              {/* Language Dropdown */}
              <AnimatePresence>
                {isLanguageOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="absolute right-0 z-50 w-56 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden"
                    role="listbox"
                  >
                    <div className="py-1" role="listbox" aria-label="Select language">
                      {languageOptions.map((language) => {
                        const isSelected = language.value === i18n.language
                        return (
                          <button
                            key={language.value}
                            type="button"
                            onClick={() => changeLanguage(language.value)}
                            className={cn(
                              'w-full px-3 py-2 flex items-center justify-between hover:bg-gray-700 transition-colors duration-150',
                              isSelected && 'bg-gray-700'
                            )}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="text-lg"
                                role="img"
                                aria-label={language.label}
                              >
                                {language.flag}
                              </span>
                              <span
                                className={cn(
                                  'text-sm font-medium',
                                  isSelected ? 'text-pink-400' : 'text-gray-300'
                                )}
                              >
                                {language.nativeName}
                              </span>
                            </div>
                            {isSelected && <Check className="w-4 h-4 text-pink-400" />}
                          </button>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Currency Selector */}
            <div ref={currencyRef} className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2"
                aria-haspopup="listbox"
                aria-expanded={isCurrencyOpen}
              >
                <span
                  className="text-lg flex-shrink-0"
                  role="img"
                  aria-label={selectedCurrency.name}
                >
                  {selectedCurrency.flag}
                </span>
                <span className="whitespace-nowrap">{selectedCurrency.code}</span>
              </button>

              {/* Currency Dropdown */}
              <AnimatePresence>
                {isCurrencyOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="absolute right-0 z-50 w-56 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden"
                    role="listbox"
                  >
                    <div className="py-1" role="listbox" aria-label="Select currency">
                      {currencyOptions.map((curr) => {
                        const isSelected = curr.code === currency
                        return (
                          <button
                            key={curr.code}
                            type="button"
                            onClick={() => changeCurrency(curr.code)}
                            className={cn(
                              'w-full px-3 py-2 flex items-center justify-between hover:bg-gray-700 transition-colors duration-150',
                              isSelected && 'bg-gray-700'
                            )}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="text-lg"
                                role="img"
                                aria-label={curr.name}
                              >
                                {curr.flag}
                              </span>
                              <span
                                className={cn(
                                  'text-sm font-medium',
                                  isSelected ? 'text-accent-gold' : 'text-gray-300'
                                )}
                              >
                                {curr.code} ({curr.symbol})
                              </span>
                            </div>
                            {isSelected && <Check className="w-4 h-4 text-accent-gold" />}
                          </button>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Theme Toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              className="hidden sm:flex p-2 items-center justify-center text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              aria-label={
                theme === 'light'
                  ? t('settings.darkMode', 'Dark mode')
                  : t('settings.lightMode', 'Light mode')
              }
              title={
                theme === 'light'
                  ? t('settings.darkMode', 'Dark mode')
                  : t('settings.lightMode', 'Light mode')
              }
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>

            {/* Profile Button */}
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="hidden sm:flex p-2 items-center justify-center text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              aria-label={t('profile.title', 'My Profile')}
              title={t('profile.title', 'My Profile')}
            >
              <User className="w-5 h-5" />
            </button>

            {/* Logout Button */}
            <button
              type="button"
              onClick={handleLogout}
              className="hidden sm:flex p-2 items-center justify-center text-gray-300 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors"
              aria-label={t('auth.logout', 'Log out')}
              title={t('auth.logout', 'Log out')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
